import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const SystemSettingsContext = createContext();

export const useSystemSettings = () => {
  const context = useContext(SystemSettingsContext);
  if (!context) {
    throw new Error('useSystemSettings must be used within a SystemSettingsProvider');
  }
  return context;
};

export const SystemSettingsProvider = ({ children }) => {
  
  const [socialLinksEnabled, setSocialLinksEnabled] = useState(true);
  const [socialLinks, setSocialLinks] = useState({
    instagram: { enabled: false, url: '' },
    facebook: { enabled: false, url: '' },
    youtube: { enabled: false, url: '' },
    linkedin: { enabled: false, url: '' },
    twitter: { enabled: false, url: '' }
  });
  const [loading, setLoading] = useState(true);
  const [global404Enabled, setGlobal404Enabled] = useState(false);
  const [global404Loading, setGlobal404Loading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadGlobal404Status = async () => {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('"404"')
        .eq('id', true)
        .maybeSingle();

      if (isMounted) {
        // Keep the site available if the settings table has not been created yet.
        setGlobal404Enabled(!error && data?.['404'] === true);
        setGlobal404Loading(false);
      }
    };

    loadGlobal404Status();

    const channel = supabase
      .channel('admin-settings-404')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'admin_settings',
        filter: 'id=eq.true'
      }, (payload) => {
        if (isMounted) {
          setGlobal404Enabled(payload.eventType !== 'DELETE' && payload.new?.['404'] === true);
        }
      })
      .subscribe();

    return () => {
      isMounted = false;
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchSocialLinksStatus = async () => {
    try {
      // Get from localStorage first
      const localSettings = localStorage.getItem('system_settings');
      if (localSettings) {
        const parsed = JSON.parse(localSettings);
        if (parsed.social_links_enabled !== undefined) {
          setSocialLinksEnabled(parsed.social_links_enabled);
        }
        if (parsed.social_links) {
          setSocialLinks(parsed.social_links);
        }
        setLoading(false);
        return;
      }

      // If no localStorage setting, default to true
      setSocialLinksEnabled(true);
    } catch (error) {// Keep default value (true) on error
      setSocialLinksEnabled(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSocialLinksStatus();
  }, []);

  const updateSocialLinksEnabled = (enabled) => {
    setSocialLinksEnabled(enabled);
    // Update localStorage
    const currentSettings = JSON.parse(localStorage.getItem('system_settings') || '{}');
    localStorage.setItem('system_settings', JSON.stringify({
      ...currentSettings,
      social_links_enabled: enabled
    }));
  };

  const value = {
    socialLinksEnabled,
    socialLinks,
    setSocialLinksEnabled: updateSocialLinksEnabled,
    loading,
    global404Enabled,
    global404Loading,
    refreshSettings: fetchSocialLinksStatus
  };

  return (
    <SystemSettingsContext.Provider value={value}>
      {children}
    </SystemSettingsContext.Provider>
  );
};
